import React, { useRef, useState, useEffect } from 'react';
import CompanyLogo from '../../../../components/ui/CompanyLogo';

interface InterviewVideoQuestionProps {
  companyName: string;
  companyLogo: string;
  location: string;
  onWatchVideo: () => void;
  question: string;
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: (videoBlob: Blob | null) => void;
  maxDuration?: number; // Max recording duration in seconds (default 60)
}

export default function InterviewVideoQuestion({
  companyName,
  companyLogo,
  location,
  onWatchVideo,
  question,
  currentStep,
  totalSteps,
  onBack,
  onNext,
  maxDuration = 60,
}: InterviewVideoQuestionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const handleStartRecording = () => {
    setRecordedChunks([]);
    setVideoUrl(null);
    setRecordingTime(0);
    setCountdown(3);
    
    // Clear any existing recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    let count = 3;
    const timer = setInterval(() => {
      count--;
      setCountdown(count);
      if (count === 0) {
        clearInterval(timer);
        setCountdown(null);
        startRecordingActual();
      }
    }, 1000);
  };

  const startRecordingActual = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks(prev => [...prev, event.data]);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
      };
      
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            handleStopRecording();
          }
          return newTime;
        });
      }, 1000);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setHasRecorded(true);
      
      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      // Create blob from chunks immediately for preview
      setTimeout(() => {
        const blob = new Blob(recordedChunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
      }, 100);
    }
  };

  const handlePlayPreview = () => {
    if (videoRef.current && videoUrl) {
      videoRef.current.srcObject = null;
      videoRef.current.src = videoUrl;
      videoRef.current.play();
      setIsPlaying(true);
      videoRef.current.onended = () => setIsPlaying(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* HEADER */}
      <div className="flex-shrink-0 bg-white px-5 pt-[14px]">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-2">
            <CompanyLogo src={companyLogo} alt={companyName} className="w-[57px] h-[57px] rounded" />
            <div className="flex flex-col justify-center">
              <h1 className="text-[19px] font-bold text-[#28292d]">{companyName}</h1>
              <p className="text-[17px] font-normal text-[#28292d]">{location}</p>
            </div>
          </div>
          <button onClick={onWatchVideo} className="flex items-center gap-1 self-center">
            <span className="text-[16px] font-normal text-[#0156FF]">Watch Video</span>
          </button>
        </div>
        <div className="h-px bg-[#b9babf] mx-4" />
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-20">
        <p className="text-[14px] font-bold text-[#8E8E8F] mb-2">
          Question {currentStep} of {totalSteps}
        </p>
        <h2 className="text-[20px] font-bold text-[#28292d] mb-6 leading-tight">
          {question}
        </h2>

        {/* Video Preview/Recorder */}
        <div className="relative w-full aspect-video bg-black rounded-[16px] overflow-hidden mb-4">
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            muted={!isPlaying}
            className="w-full h-full object-cover" 
          />
          
          {/* Countdown Overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <span className="text-white text-6xl font-bold">{countdown}</span>
            </div>
          )}

          {/* Recording Indicator with Countdown */}
          {isRecording && (
            <>
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-bold">REC</span>
              </div>
              <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full font-mono text-lg font-bold">
                {maxDuration - recordingTime}s
              </div>
            </>
          )}

          {/* Play Button Overlay */}
          {hasRecorded && !isRecording && !isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <button 
                onClick={handlePlayPreview}
                className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <svg className="w-8 h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Record Control */}
        <div className="flex justify-center mb-4">
          {!isRecording && !hasRecorded ? (
            <button
              onClick={handleStartRecording}
              className="w-16 h-16 rounded-full border-4 border-[#0156FF] flex items-center justify-center"
            >
              <div className="w-12 h-12 bg-[#0156FF] rounded-full" />
            </button>
          ) : isRecording ? (
            <button
              onClick={handleStopRecording}
              className="w-16 h-16 rounded-full border-4 border-red-500 flex items-center justify-center"
            >
              <div className="w-8 h-8 bg-red-500 rounded-[4px]" />
            </button>
          ) : (
            <button
              onClick={handleStartRecording} // Retake
              className="bg-[#0156FF] text-white text-[16px] font-bold rounded-full w-[90px] h-[40px] flex items-center justify-center active:scale-[0.98] transition-transform"
            >
              Retake
            </button>
          )}
      </div>
      </div>

      {/* BOTTOM BUTTONS */}
      <div className="fixed bottom-0 left-0 right-0 px-5 py-4 flex items-center justify-between bg-white border-t border-[#eeeeee] z-50">
        <button onClick={onBack} className="bg-[#8E8E8F] text-white text-[16px] font-bold rounded-full w-[90px] h-[40px] flex items-center justify-center">
          Back
        </button>
        <button
          onClick={() => onNext(null)} // Pass actual blob in production
          className={`${hasRecorded ? 'bg-[#0156FF]' : 'bg-[#b9babf]'} text-white text-[16px] font-bold rounded-full w-[90px] h-[40px] flex items-center justify-center`}
          disabled={!hasRecorded}
        >
          Finish
        </button>
      </div>
    </div>
  );
}

